/**
 * The manifest (`@/lib/navItems`) owns keys, labels and grouping; this attaches the screen.
 *
 * Every screen here reads from the database. Until 2026-09-25 this file also held seventeen
 * static prototypes built from `src/data/`, fourteen of which were in the menu; the owner took
 * them out, then out of the build. What they showed - a geography of West Bengal while the real
 * data is Karnataka, a toolbar of buttons with no handler - is not something a compliance record
 * should sit beside. `PROTOTYPE_KEYS` in the manifest names them and a test keeps them out.
 *
 * A screen takes no props. It reads what it needs through `@/api/resources` under the caller's
 * own permissions; there is no shell-level state for it to inherit.
 */
import { NAV_ITEMS } from '@/lib/navItems';
import type { NavigationEntry } from '@/pages/pageRuntime';

import AccessScreen from '@/screens/AccessScreen';
import AccidentsScreen from '@/screens/AccidentsScreen';
import AnnualMonitoringScreen from '@/screens/AnnualMonitoringScreen';
import ApprovalInboxScreen from '@/screens/ApprovalInboxScreen';
import AuditTimelineScreen from '@/screens/AuditTimelineScreen';
import ChamberNormsScreen from '@/screens/ChamberNormsScreen';
import ChamberLoadsScreen from '@/screens/ChamberLoadsScreen';
import ProcessRunsScreen from '@/screens/ProcessRunsScreen';
import TreatmentChemicalsScreen from '@/screens/TreatmentChemicalsScreen';
import ChambersScreen from '@/screens/ChambersScreen';
import CommsScreen from '@/screens/CommsScreen';
import ConsentScreen from '@/screens/ConsentScreen';
import ConsultationsScreen from '@/screens/ConsultationsScreen';
import ContractorsScreen from '@/screens/ContractorsScreen';
import DashboardScreen from '@/screens/DashboardScreen';
import DocumentTypesScreen from '@/screens/DocumentTypesScreen';
import EvidencePacksScreen from '@/screens/EvidencePacksScreen';
import FarmerRegisterScreen from '@/screens/FarmerRegisterScreen';
import FarmerStatementScreen from '@/screens/FarmerStatementScreen';
import GeographyScreen from '@/screens/GeographyScreen';
import GrievancesScreen from '@/screens/GrievancesScreen';

import InputsScreen from '@/screens/InputsScreen';
import InternalAuditsScreen from '@/screens/InternalAuditsScreen';
import InventoryScreen from '@/screens/InventoryScreen';
import IrrigationScreen from '@/screens/IrrigationScreen';
import KycRequirementsScreen from '@/screens/KycRequirementsScreen';
import KycScreen from '@/screens/KycScreen';
import MyAlertsScreen from '@/screens/MyAlertsScreen';
import OnboardingScreen from '@/screens/OnboardingScreen';
import OrganisationsScreen from '@/screens/OrganisationsScreen';
import EcosystemScreen from '@/screens/EcosystemScreen';
import EngagementsScreen from '@/screens/EngagementsScreen';
import OrgSettingsScreen from '@/screens/OrgSettingsScreen';
import PestsScreen from '@/screens/PestsScreen';
import PlotMapScreen from '@/screens/PlotMapScreen';
import LandUnitsScreen from '@/screens/LandUnitsScreen';
import PlotScreen from '@/screens/PlotScreen';
import ReferenceDataScreen from '@/screens/ReferenceDataScreen';
import ReferencesScreen from '@/screens/ReferencesScreen';
import RenewalsScreen from '@/screens/RenewalsScreen';
import SatelliteOpsScreen from '@/screens/SatelliteOpsScreen';
import SatelliteScreen from '@/screens/SatelliteScreen';
import SilvicultureScreen from '@/screens/SilvicultureScreen';
import TreeMonitoringScreen from '@/screens/TreeMonitoringScreen';
import SaplingIssuesScreen from '@/screens/SaplingIssuesScreen';
import HarvestPermitsScreen, { InternalPermitsScreen } from '@/screens/HarvestPermitsScreen';
import HarvestReportScreen from '@/screens/HarvestReportScreen';
import HarvestLabourScreen from '@/screens/HarvestLabourScreen';
import YieldStatementScreen from '@/screens/YieldStatementScreen';
import PlotConsentsScreen from '@/screens/PlotConsentsScreen';
import SaleInvoicesScreen from '@/screens/SaleInvoicesScreen';
import SaleDocketsScreen from '@/screens/SaleDocketsScreen';
import HsnCodesScreen from '@/screens/HsnCodesScreen';
import SitesScreen from '@/screens/SitesScreen';
import SoilTestsScreen from '@/screens/SoilTestsScreen';
import RiskAssessmentScreen from '@/screens/RiskAssessmentScreen';
import StakeholdersScreen from '@/screens/StakeholdersScreen';
import TeamsScreen from '@/screens/TeamsScreen';
import TradesScreen, { FarmerSalesScreen } from '@/screens/TradesScreen';
import WorkersScreen from '@/screens/WorkersScreen';
import WorkflowsScreen from '@/screens/WorkflowsScreen';

import type { ReactElement } from 'react';

/**
 * Every key the menu can carry, and the screen behind it.
 *
 * A key in the manifest with no entry here throws at import, which is the point: a screen cannot
 * ship half-wired. The reverse is allowed - `users` and `access` are two doors into one screen.
 */
const NAV_COMPONENTS: Record<string, () => ReactElement> = {
  dashboard: DashboardScreen,

  farmers: FarmerRegisterScreen,
  statement: FarmerStatementScreen,
  references: ReferencesScreen,

  kyc: KycScreen,
  'consent-capture': ConsentScreen,
  'plot-consents': PlotConsentsScreen,
  stakeholders: StakeholdersScreen,

  sites: SitesScreen,
  contractors: ContractorsScreen,

  plots: PlotScreen,
  'plot-map': PlotMapScreen,
  'land-units': LandUnitsScreen,
  inventory: InventoryScreen,
  // The six practice registers. Each reads the same api view its section inside the plot record
  // writes to, so a row added on either is the same row.
  silviculture: SilvicultureScreen,
  inputs: InputsScreen,
  irrigation: IrrigationScreen,
  'soil-tests': SoilTestsScreen,
  'risk-assessment': RiskAssessmentScreen,
  pests: PestsScreen,
  trades: TradesScreen,
  chambers: ChambersScreen,
  'chamber-norms': ChamberNormsScreen,
  'chamber-loads': ChamberLoadsScreen,
  'treatment-chemicals': TreatmentChemicalsScreen,
  'process-runs': ProcessRunsScreen,
  workers: WorkersScreen,

  'sapling-issues': SaplingIssuesScreen,
  'harvest-permits': HarvestPermitsScreen,
  'farmer-sales': FarmerSalesScreen,
  'internal-permits': InternalPermitsScreen,
  'harvest-report': HarvestReportScreen,
  'harvest-labour': HarvestLabourScreen,
  'yield-statement': YieldStatementScreen,
  'sale-invoices': SaleInvoicesScreen,
  'sale-dockets': SaleDocketsScreen,
  'hsn-codes': HsnCodesScreen,
  growth: TreeMonitoringScreen,
  satellite: SatelliteScreen,
  'satellite-ops': SatelliteOpsScreen,
  'evidence-packs': EvidencePacksScreen,
  renewals: RenewalsScreen,
  'annual-monitoring': AnnualMonitoringScreen,
  'internal-audits': InternalAuditsScreen,
  consultations: ConsultationsScreen,
  complaints: GrievancesScreen,
  accidents: AccidentsScreen,

  inbox: ApprovalInboxScreen,
  'my-alerts': MyAlertsScreen,
  comms: CommsScreen,

  audit: AuditTimelineScreen,

  ecosystem: EcosystemScreen,
  engagements: EngagementsScreen,
  organisation: OrgSettingsScreen,
  geography: GeographyScreen,
  doctypes: DocumentTypesScreen,
  'kyc-requirements': KycRequirementsScreen,
  workflows: WorkflowsScreen,
  config: ReferenceDataScreen,
  organisations: OrganisationsScreen,
  users: AccessScreen,
  onboarding: OnboardingScreen,
  access: AccessScreen,
  teams: TeamsScreen,
};

export const navigation: NavigationEntry[] = NAV_ITEMS.map((item) => {
  const component = NAV_COMPONENTS[item.key];
  if (!component) throw new Error(`Vanam: navigation entry "${item.key}" has no screen component.`);
  return { ...item, component };
});

/** Navigation grouped for the sidebar, preserving declaration order. */
export const navigationGroups: Array<{ group: string; items: NavigationEntry[] }> = navigation.reduce(
  (groups: Array<{ group: string; items: NavigationEntry[] }>, item) => {
    const existing = groups.find((entry) => entry.group === item.group);
    if (existing) existing.items.push(item);
    else groups.push({ group: item.group, items: [item] });
    return groups;
  },
  [],
);
